// ✅ THEME + UX FIXES (NO business/API logic changes)
// FIX 1: Match StudentAttendanceReport theme exactly
// FIX 2: Rounded corners for ALL tables (outer + nested) even when horizontally scrollable
// FIX 3: "Second dropdown column names not visible" -> fix Autocomplete/TextField label visibility
//        - Set default label color (blue) + focused label (orange)
//        - Ensure label is above chips + visible in shrink state
//        - Also style the Autocomplete popup/paper so options text stays visible
// ✅ Keeps your row-click toggle + icon stopPropagation, and all API logic unchanged

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Typography,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  Checkbox,
  Stack,
  Autocomplete,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteIcon from "@mui/icons-material/Delete";
import { API_URL, API_UPLOAD_URL } from "./config";

export default function StudentAttendanceByStudent() {
  const token = localStorage.getItem("auth");

  // ✅ Theme constants (match report page)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = {
    ...surfaceSx,
    p: 2,
    position: "relative",
    overflow: "hidden",
    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 6,
      background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
      opacity: 0.95,
    },
  };

  // ✅ INPUTS: outline + focused label ORANGE + (important) default label visible
  // This fixes “dropdown column names not visible” (Autocomplete labels).
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },

    // ✅ label visibility (normal + shrink + focused)
    "& .MuiInputLabel-root": {
      color: "rgba(25,118,210,0.88)", // visible blue on white
      fontWeight: 800,
      zIndex: 1,
      backgroundColor: "transparent",
      px: 0.25,
    },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
      color: "rgba(25,118,210,0.88)",
    },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const iconBtnSx = {
    borderRadius: 2,
    color: "rgba(25,118,210,0.95)",
    "&:hover": { backgroundColor: "rgba(240,120,40,0.10)", color: THEME_ORANGE },
  };

  // ✅ FIX: Fully-rounded tables even with horizontal scroll
  const tableCardSx = {
    ...surfaceSx,
    mt: 2,
    p: 0,
    overflow: "hidden", // clips header corners
  };

  const scrollClipSx = {
    overflowX: "auto",
    overflowY: "hidden",
    borderRadius: 2,
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };

  const zebraRowSx = (idx) => ({
    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#FFFFFF",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 1 },
  });

  const subTheadRowSx = {
    backgroundColor: "rgba(25,118,210,0.10)",
    "& th": {
      fontWeight: 900,
      color: "rgba(255, 255, 255, 1)",
      //color: "rgba(25,118,210,0.95)",
      borderBottom: "1px solid rgba(25,118,210,0.10)",
      whiteSpace: "nowrap",
      //textAlign: "center",
      py: 1,
    },
  };

  const checkboxSx = {
    //color: "rgba(25,118,210,0.75)",
    color: "rgba(0, 0, 0, 1)",
    "&.Mui-checked": { color: THEME_ORANGE },
  };

  const chipSx = {
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  // ✅ Autocomplete styling (matches report page) + popup/paper fixes for visibility
  const autoCompleteSx = {
    ...inputSx,

    "& .MuiAutocomplete-tag": {
      borderRadius: 8,
      backgroundColor: "rgba(25,118,210,0.06)",
      border: "1px solid rgba(25,118,210,0.18)",
      fontWeight: 800,
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
      color: "rgba(25,118,210,0.75)",
      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
    },

    // ✅ dropdown list/paper (keeps text readable + rounded)
    "& .MuiAutocomplete-paper": {
      borderRadius: 12,
      border: `1px solid ${BORDER}`,
      boxShadow: "0 10px 30px rgba(0,0,0,0.10)",
      overflow: "hidden",
    },
    "& .MuiAutocomplete-option": {
      color: "rgba(0,0,0,0.87)",
      fontWeight: 700,
      "&[aria-selected='true']": { backgroundColor: "rgba(25,118,210,0.08)" },
      "&.Mui-focused": { backgroundColor: "rgba(240,120,40,0.08)" },
    },
  };

  // ===== state (business logic unchanged) =====
  const [campusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");

  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      : "";

  const [loadingLookups, setLoadingLookups] = useState(false);
  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
  });

  const [subcampusCodes, setSubcampusCodes] = useState([]);
  const [degreeCodes, setDegreeCodes] = useState([]);
  const [branchCodes, setBranchCodes] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [studentIdInput, setStudentIdInput] = useState("");
  const [studentIds, setStudentIds] = useState([]);

  const [loadingTop, setLoadingTop] = useState(false);
  const [rows, setRows] = useState([]);
  const [openStu, setOpenStu] = useState({});
  const [details, setDetails] = useState({});
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(payload.campusCode || payload.CampusCode || "");
      setUsername(payload.username || payload.name || "");
    } catch (e) {
      console.error("JWT decode error", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const pct = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, n));
  };

  const parseIDs = (txt) =>
    Array.from(
      new Set(
        (txt || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const hasAnyFilter =
    subcampusCodes.length > 0 ||
    degreeCodes.length > 0 ||
    branchCodes.length > 0 ||
    semesters.length > 0 ||
    studentIds.length > 0;

  const buildQueryParams = () => {
    const params = { CampusCode: campusCode };
    if (subcampusCodes.length) params.SubCampusCode = subcampusCodes.join(",");
    if (degreeCodes.length) params.DegreeCode = degreeCodes.join(",");
    if (branchCodes.length) params.BranchCode = branchCodes.join(",");
    if (semesters.length) params.Semester = semesters.join(",");
    if (studentIds.length) params.StudentIDs = studentIds.join(",");
    return params;
  };

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;
    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
        });
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  const fetchTop = async () => {
    if (!campusCode || !hasAnyFilter) {
      setRows([]);
      setOpenStu({});
      setDetails({});
      return;
    }
    setLoadingTop(true);
    setRows([]);
    setOpenStu({});
    setDetails({});
    setSelectedIds(new Set());
    try {
      const { data } = await axios.get(`${API_URL}/api/UpdateAttendance/students/summary`, {
        ...authHeader,
        params: buildQueryParams(),
      });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to load attendance summary." });
    } finally {
      setLoadingTop(false);
    }
  };

  const toggleStudent = async (studentId) => {
    const nextOpen = !openStu[studentId];
    setOpenStu((p) => ({ ...p, [studentId]: nextOpen }));
    if (nextOpen && !details[studentId]) {
      setDetails((p) => ({ ...p, [studentId]: { loading: true, rows: [] } }));
      try {
        const { data } = await axios.get(
          `${API_URL}/api/UpdateAttendance/students/${encodeURIComponent(studentId)}/dates`,
          { ...authHeader, params: buildQueryParams() }
        );
        setDetails((p) => ({
          ...p,
          [studentId]: { loading: false, rows: Array.isArray(data) ? data : [] },
        }));
      } catch (e) {
        console.error(e);
        setDetails((p) => ({ ...p, [studentId]: { loading: false, rows: [] } }));
        setToast({
          open: true,
          type: "error",
          msg: `Failed to load daily records for ${studentId}.`,
        });
      }
    }
  };

  const inSelected = (id) => selectedIds.has(id);
  const toggleSelect = (id) =>
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });

  const toggleSelectAllForStudent = (studentId) => {
    const r = details[studentId]?.rows || [];
    const ids = r.map((x) => x.ClassAttendanceID).filter(Boolean);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    setSelectedIds((prev) => {
      const s = new Set(prev);
      if (allSelected) ids.forEach((id) => s.delete(id));
      else ids.forEach((id) => s.add(id));
      return s;
    });
  };

  const deleteSelected = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) {
      setToast({ open: true, type: "info", msg: "Select at least one row to delete." });
      return;
    }
    if (!window.confirm(`Delete ${ids.length} record(s)? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API_URL}/api/UpdateAttendance/classattendance`, {
        ...authHeader,
        data: { ids, Username: username },
      });
      setToast({ open: true, type: "success", msg: `Deleted ${ids.length} record(s).` });
      fetchTop();
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Delete failed." });
    }
  };

  const onStudentInputChange = (e) => {
    const txt = e.target.value;
    setStudentIdInput(txt);
    setStudentIds(parseIDs(txt));
  };

  const renderMultiDropdown = (label, value, setValue, optionsList, minWidth = 200) => (
    <Autocomplete
      multiple
      options={optionsList}
      value={value}
      onChange={(_, newVal) => setValue(newVal.map((v) => String(v).trim()).filter(Boolean))}
      disableCloseOnSelect
      filterSelectedOptions
      openOnFocus
      loading={loadingLookups}
      sx={autoCompleteSx}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          sx={inputSx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loadingLookups ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      style={{ minWidth }}
    />
  );

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Update Student Attendance
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter students, drill into absent records, and delete wrong entries
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              sx={primaryBtnSx}
              onClick={fetchTop}
              disabled={loadingTop || !hasAnyFilter}
            >
              {loadingTop ? "Loading…" : "Search"}
            </Button>

            <Button
              variant="outlined"
              sx={outlinedBtnSx}
              startIcon={<DeleteIcon />}
              onClick={deleteSelected}
              disabled={selectedIds.size === 0}
            >
              Delete
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" },
                  gap: 1,
                  mb: 2,
                }}
              >
                {renderMultiDropdown(
                  "SubCampusCode",
                  subcampusCodes,
                  setSubcampusCodes,
                  options.SubCampusCode,
                  220
                )}
                {renderMultiDropdown("DegreeCode", degreeCodes, setDegreeCodes, options.DegreeCode)}
                {renderMultiDropdown("BranchCode", branchCodes, setBranchCodes, options.BranchCode)}
                {renderMultiDropdown("Semester", semesters, setSemesters, options.SemesterCode)}
              </Box>

              <Box sx={{ flex: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Student IDs (comma / space / newline)"
                  placeholder="e.g. STUID01, STUID02"
                  value={studentIdInput}
                  onChange={onStudentInputChange}
                  sx={inputSx}
                />

                {studentIds.length > 0 && (
                  <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                    {studentIds.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={id}
                        onDelete={() => {
                          const next = studentIds.filter((x) => x !== id);
                          setStudentIds(next);
                          setStudentIdInput(next.join(", "));
                        }}
                        sx={{ ...chipSx, mb: 1 }}
                      />
                    ))}
                  </Stack>
                )}
              </Box>
            </Box>
          </Paper>

          {/* ✅ MAIN TABLE: rounded outer + inner scroll clip (fixes corners even on overflow) */}
          <Paper elevation={0} sx={tableCardSx}>
            <Box sx={scrollClipSx}>
              <TableContainer component="div">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={theadRowSx}>
                      <TableCell sx={{ width: 48 }} />
                      {[
                        "StudentID",
                        "SubCampus",
                        "Degree",
                        "Branch",
                        "Semester",
                        "TotalClasses",
                        "Attended",
                        "Absent",
                        "Attendance %",
                      ].map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loadingTop ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
                            <CircularProgress size={20} />
                            <Typography>Loading…</Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10}>
                          <Typography sx={{ p: 2 }}>
                            {hasAnyFilter
                              ? "No data matches your filters."
                              : "Add a filter and click Search to see results."}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((r, idx) => {
                        const sid = r.StudentID;
                        const isOpen = !!openStu[sid];
                        const d = details[sid];

                        return (
                          <React.Fragment key={`${sid}-${idx}`}>
                            {/* ✅ Click anywhere on this row to toggle */}
                            <TableRow
                              sx={{ ...zebraRowSx(idx), cursor: "pointer" }}
                              onClick={() => toggleStudent(sid)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  toggleStudent(sid);
                                }
                              }}
                              aria-expanded={isOpen}
                            >
                              <TableCell>
                                {/* ✅ Prevent double toggle when clicking the icon */}
                                <IconButton
                                  size="small"
                                  sx={iconBtnSx}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleStudent(sid);
                                  }}
                                >
                                  {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                                </IconButton>
                              </TableCell>

                              <TableCell>{r.StudentID}</TableCell>
                              <TableCell>{r.SubCampusCode}</TableCell>
                              <TableCell>{r.DegreeCode}</TableCell>
                              <TableCell>{r.BranchCode}</TableCell>
                              <TableCell>{r.Semester}</TableCell>
                              <TableCell>{Number(r.TotalClasses || 0)}</TableCell>
                              <TableCell>{Number(r.AttendedClasses || 0)}</TableCell>
                              <TableCell>{Number(r.AbsentClasses || 0)}</TableCell>
                              <TableCell>{pct(r.AttendancePercentage).toFixed(2)}%</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell colSpan={10} sx={{ p: 0, border: 0 }}>
                                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                  <Box sx={{ m: 1, ...surfaceSx, overflow: "hidden" }}>
                                    <Box sx={{ p: 1.5 }}>
                                      <Typography
                                        variant="subtitle2"
                                        sx={{ fontWeight: 900, color: THEME_ORANGE }}
                                      >
                                        Absent records • {sid}
                                      </Typography>

                                      {d?.loading ? (
                                        <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
                                          <CircularProgress size={18} />
                                          <Typography>Loading…</Typography>
                                        </Box>
                                      ) : (d?.rows || []).length === 0 ? (
                                        <Typography sx={{ p: 2 }}>No absent records.</Typography>
                                      ) : (
                                        // ✅ NESTED TABLE: also rounded + scroll clip
                                        <Paper elevation={0} sx={{ ...tableCardSx, mt: 1 }}>
                                          <Box sx={scrollClipSx}>
                                            <Table size="small">
                                              <TableHead>
                                                <TableRow sx={subTheadRowSx} >
                                                  <TableCell width={48}>
                                                    <Checkbox
                                                      size="small"
                                                      sx={{ ...checkboxSx, p: 0.25 }}
                                                      onClick={(e) => e.stopPropagation()}
                                                      onChange={() => toggleSelectAllForStudent(sid)}
                                                      checked={
                                                        (d.rows || []).every((x) =>
                                                          selectedIds.has(x.ClassAttendanceID)
                                                        ) && (d.rows || []).length > 0
                                                      }
                                                      indeterminate={
                                                        (d.rows || []).some((x) =>
                                                          selectedIds.has(x.ClassAttendanceID)
                                                        ) &&
                                                        !(d.rows || []).every((x) =>
                                                          selectedIds.has(x.ClassAttendanceID)
                                                        )
                                                      }
                                                    />
                                                  </TableCell>
                                                  <TableCell>StudentID</TableCell>
                                                  <TableCell>AttendanceDate</TableCell>
                                                  <TableCell>SubjectNameCode</TableCell>
                                                  <TableCell>Section</TableCell>
                                                </TableRow>
                                              </TableHead>

                                              <TableBody>
                                                {(d.rows || []).map((x, j) => (
                                                  <TableRow key={`${sid}-${j}`} sx={zebraRowSx(j)}>
                                                    <TableCell>
                                                      <Checkbox
                                                        size="small"
                                                        sx={{ ...checkboxSx, p: 0.25 }}
                                                        checked={inSelected(x.ClassAttendanceID)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        onChange={() => toggleSelect(x.ClassAttendanceID)}
                                                      />
                                                    </TableCell>
                                                    <TableCell>{x.StudentID}</TableCell>
                                                    <TableCell>{convertToISTDate(x.AttendanceDate)}</TableCell>
                                                    <TableCell>{x.SubjectNameCode}</TableCell>
                                                    <TableCell>{x.Section}</TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          </Box>
                                        </Paper>
                                      )}
                                    </Box>
                                  </Box>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Paper>

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
